import { useDispatch } from 'react-redux';
import { MeasurementSystem } from '@fleet-bffs/api-contract';
import logger from '~/utils/logger/logger';
import type { AppDispatch } from '~/store/Store';
import AssetTypeConstants from '~/constants/asset-type-constants';
import ModalStep from '~/domain/add-asset/data/modal-step';
import { applyMeasurementSystem } from '~/ducks/authentication/apply-measurement-system';
import { clearFleetServiceWorkerCaches } from '~/utils/service-worker-utils/service-worker-utils';
import { RoboticMowerBleConnectionStatusEnum } from '../models/ble-connection-status';
import { DeviceIdiom, DevicePlatform } from '../models/native-version';
import { WebCommandEnum } from '../models/commands';
import { dispatchWebCommand, setOnWebView, setOnWebViewInitialized } from '../store/reducer';

declare global {
  interface Window {
    [WebCommandEnum.InitializeWebView]: (
      platform: DevicePlatform,
      version: string,
      build: string,
      deviceIdiom: DeviceIdiom
    ) => void;
    [WebCommandEnum.Navigate]: (path: string, search?: string, browserState?: string) => void;
    refreshTokenResponseResolver?: (status: number) => void;
  }
}

export const useNativeAppListener = () => {
  const dispatch = useDispatch<AppDispatch>();
  window[WebCommandEnum.InitializeWebView] = (
    platform: DevicePlatform,
    version: string,
    build: string,
    deviceIdiom: DeviceIdiom
  ) => {
    logger.log(`native/window[${WebCommandEnum.InitializeWebView}]`);
    dispatch(setOnWebView({ build, platform, version, deviceIdiom }));
  };
  window[WebCommandEnum.ActivateWebView] = () => {
    logger.log(`native/window[${WebCommandEnum.ActivateWebView}]`);
    dispatch(setOnWebViewInitialized());
  };

  window[WebCommandEnum.RequestAppLoaded] = () => {
    logger.log(`native/window[${WebCommandEnum.RequestAppLoaded}]`);
    dispatch(dispatchWebCommand({ name: WebCommandEnum.RequestAppLoaded }));
  };

  window[WebCommandEnum.Navigate] = (path, search?, browserState?) => {
    logger.debug(`native/window[${WebCommandEnum.Navigate}]`, path, search, browserState);
    if (!path) {
      logger.warn(`native/window[${WebCommandEnum.Navigate}] called without path — ignoring`);
      return;
    }
    dispatch(
      dispatchWebCommand({
        name: WebCommandEnum.Navigate,
        params: { path, search, data: browserState },
      })
    );
  };

  window[WebCommandEnum.RefreshAssetDetails] = () => {
    logger.debug(`native/window[${WebCommandEnum.RefreshAssetDetails}]`);
    dispatch(
      dispatchWebCommand({
        name: WebCommandEnum.RefreshAssetDetails,
      })
    );
  };

  window[WebCommandEnum.RefreshDashboard] = () => {
    logger.debug(`native/window[${WebCommandEnum.RefreshDashboard}]`);
    dispatch(
      dispatchWebCommand({
        name: WebCommandEnum.RefreshDashboard,
      })
    );
  };

  window[WebCommandEnum.RefreshInventory] = () => {
    logger.debug(`native/window[${WebCommandEnum.RefreshInventory}]`);
    dispatch(
      dispatchWebCommand({
        name: WebCommandEnum.RefreshInventory,
      })
    );
  };

  window[WebCommandEnum.OpenProductList] = (assetType: AssetTypeConstants, step: ModalStep) => {
    logger.debug(`native/window[${WebCommandEnum.OpenProductList}]`);
    if (!assetType) {
      logger.warn(
        `native/window[${WebCommandEnum.OpenProductList}] called without assetType — ignoring`
      );
      return;
    }
    dispatch(
      dispatchWebCommand({
        name: WebCommandEnum.OpenProductList,
        params: { assetType, step },
      })
    );
  };

  window[WebCommandEnum.RoboticMowerConnectionUpdated] = (
    assetId: string,
    connectionStatus: RoboticMowerBleConnectionStatusEnum
  ) => {
    logger.debug(`native/window[${WebCommandEnum.RoboticMowerConnectionUpdated}]`);
    dispatch(
      dispatchWebCommand({
        name: WebCommandEnum.RoboticMowerConnectionUpdated,
        params: { assetId, status: connectionStatus },
      })
    );
  };

  window[WebCommandEnum.RoboticMowerSitemapUpdated] = (assetId: string, timestamp: number) => {
    logger.debug(`native/window[${WebCommandEnum.RoboticMowerSitemapUpdated}]`);
    dispatch(
      dispatchWebCommand({
        name: WebCommandEnum.RoboticMowerSitemapUpdated,
        params: { assetId, timestamp },
      })
    );
  };

  window[WebCommandEnum.UserMeasurementSystemUpdated] = (measurementSystem: MeasurementSystem) => {
    logger.debug(
      `native/window[${WebCommandEnum.UserMeasurementSystemUpdated}]`,
      measurementSystem
    );
    dispatch(applyMeasurementSystem(measurementSystem));
  };

  window[WebCommandEnum.ReceiveRefreshTokenResponse] = (status: number) => {
    logger.log(`native/window[${WebCommandEnum.ReceiveRefreshTokenResponse}]`);
    logger.log('ReceiveRefreshTokenResponse', status);
    if (window.refreshTokenResponseResolver) {
      window.refreshTokenResponseResolver(status);
      window.refreshTokenResponseResolver = undefined;
    }
  };
  window[WebCommandEnum.ClearServiceWorkerCaches] = () => {
    logger.log(`native/window[${WebCommandEnum.ClearServiceWorkerCaches}]`);
    clearFleetServiceWorkerCaches();
  };
};
export const waitForTokenRefreshResponse = (): Promise<number> => {
  return new Promise((resolve) => {
    window.refreshTokenResponseResolver = resolve;
  });
};
